import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { cn } from './cn';

/** Nonmodal work panel that follows its host content width and leaves the page interactive. */
const DockPanel = forwardRef(function DockPanel(
  {
    expanded = true,
    trigger,
    header,
    maxWidth = 1120,
    maxHeight = '80dvh',
    bottomOffset = 0,
    'aria-label': label = 'Work panel',
    className,
    panelProps,
    children,
  },
  ref
) {
  const hostRef = useRef(null);
  const [insets, setInsets] = useState({ left: 16, right: 16 });
  useLayoutEffect(() => {
    const host = hostRef.current;
    const update = () => {
      const bounds = host?.getBoundingClientRect();
      if (!bounds || bounds.width <= 0) return;
      setInsets({
        left: Math.max(16, bounds.left),
        right: Math.max(16, window.innerWidth - bounds.right),
      });
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (host) observer?.observe(host);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div ref={hostRef} className="w-full">
      <div
        className="pointer-events-none fixed bottom-[calc(max(16px,env(safe-area-inset-bottom))_+_var(--dock-bottom-offset))] z-40 flex justify-center font-instrument"
        style={{ ...insets, '--dock-bottom-offset': `${bottomOffset}px` }}
      >
        {expanded ? (
          <section
            {...panelProps}
            ref={ref}
            aria-label={label}
            style={{ maxWidth, maxHeight }}
            className={cn(
              'pointer-events-auto flex w-full min-w-0 flex-col overflow-hidden rounded-bun-lg border border-bun-line bg-bun-surface text-bun-text shadow-bun-menu',
              className
            )}
          >
            {header && (
              <div className="shrink-0 border-b border-bun-line-soft px-[18px] py-3">{header}</div>
            )}
            <div className="min-h-0 overflow-y-auto px-[18px] py-3">
              {children}
            </div>
          </section>
        ) : (
          <div className="pointer-events-auto max-w-full">{trigger}</div>
        )}
      </div>
    </div>
  );
});

export default DockPanel;
